import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  RobotOutlined,
  ThunderboltOutlined,
  FileTextOutlined,
  ApiOutlined,
  BarChartOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import EmptyState from '../../components/shared/EmptyState/EmptyState';
import SideModal from '../../components/shared/SideModal/SideModal';
import { servicesService } from '../../services';
import { getServiceAge } from '../../utils/serviceHelpers';


interface ChatbotService {
  id: string;
  name: string;
  status: 'running' | 'stopped' | 'deploying' | 'error';
  plan: string;
  messagesLimit: number;
  documentsLimit: number;
  createdAgo: string;
  features: {
    pdfSupport?: boolean;
    excelSupport?: boolean;
    imageSupport?: boolean;
    apiAccess?: boolean;
    analyticsEnabled?: boolean;
  };
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  running: {
    label: 'Activo',
    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    icon: <CheckCircleOutlined />,
  },
  stopped: {
    label: 'Detenido',
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    icon: <PauseCircleOutlined />,
  },
  deploying: {
    label: 'Desplegando',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    icon: <ClockCircleOutlined />,
  },
  error: {
    label: 'Error',
    color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    icon: <ExclamationCircleOutlined />,
  },
};

const PLAN_COLORS: Record<string, string> = {
  starter: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  pro: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  enterprise: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
};

// Componente de tarjeta de servicio Chatbot
interface ChatbotCardProps {
  service: ChatbotService;
  onClick: () => void;
}

const ChatbotCard: React.FC<ChatbotCardProps> = ({ service, onClick }) => {
  const statusConfig = STATUS_CONFIG[service.status] || STATUS_CONFIG.stopped;
  const planColor = PLAN_COLORS[service.plan] || PLAN_COLORS.starter;

  return (
    <button
      onClick={onClick}
      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 transition-all text-left w-full"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
            <RobotOutlined className="text-lg text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">{service.name}</h3>
            <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full mt-1 ${planColor}`}>
              {service.plan.charAt(0).toUpperCase() + service.plan.slice(1)}
            </span>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${statusConfig.color}`}>
          {statusConfig.icon} {statusConfig.label}
        </span>
      </div>

      {/* Límites */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-2.5">
          <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-1">
            <ThunderboltOutlined /> Mensajes/mes
          </div>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            {service.messagesLimit.toLocaleString()}
          </p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-2.5">
          <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-1">
            <FileTextOutlined /> Documentos
          </div>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            {service.documentsLimit.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Features badges */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {service.features.apiAccess && (
          <span className="inline-flex items-center gap-1 text-[11px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
            <ApiOutlined /> API
          </span>
        )}
        {service.features.analyticsEnabled && (
          <span className="inline-flex items-center gap-1 text-[11px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
            <BarChartOutlined /> Analytics
          </span>
        )}
      </div>

      {/* Footer */}
      <div className="text-xs text-gray-500 dark:text-gray-400">
        Creado {service.createdAgo}
      </div>
    </button>
  );
};

// Componente principal de listado
const ChatbotIA: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPlan, setFilterPlan] = useState<string>('all');
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [services, setServices] = useState<ChatbotService[]>([]);
  const [loading, setLoading] = useState(true);

  // Mapear datos del API al formato local
  const mapServiceToChatbot = useCallback((serviceData: any): ChatbotService => {
    const metadata = serviceData.metadata || {};

    const mapStatus = (status: string): 'running' | 'stopped' | 'deploying' | 'error' => {
      switch (status?.toLowerCase()) {
        case 'running':
        case 'active':
          return 'running';
        case 'stopped':
          return 'stopped';
        case 'deploying':
        case 'initializing':
        case 'stacking':
        case 'creating':
        case 'starting':
        case 'restarting':
          return 'deploying';
        case 'error':
        case 'terminated':
          return 'error';
        default:
          return 'stopped';
      }
    };

    const createdAt = serviceData.createdAt
      ? (typeof serviceData.createdAt === 'string' ? new Date(serviceData.createdAt) : serviceData.createdAt)
      : new Date();

    return {
      id: serviceData.id,
      name: serviceData.name || metadata.serviceName || 'Chatbot sin nombre',
      status: mapStatus(serviceData.status),
      plan: metadata.plan || 'starter',
      messagesLimit: metadata.messagesLimit || 0,
      documentsLimit: metadata.documentsLimit || 0,
      createdAgo: getServiceAge(createdAt),
      features: metadata.features || {},
    };
  }, []);

  // Fetch services
  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await servicesService.getByCategory('AI_SERVICE');
      const mapped = response.services.map(mapServiceToChatbot);
      setServices(mapped);
    } catch (error) {
      console.error('Error fetching AI services:', error);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [mapServiceToChatbot]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // Filtrado
  const filteredServices = services.filter((s) => {
    const matchesSearch =
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.plan.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || s.status === filterStatus;
    const matchesPlan = filterPlan === 'all' || s.plan === filterPlan;
    return matchesSearch && matchesStatus && matchesPlan;
  });

  return (
    <div className="max-w-[1600px] mx-auto px-8 py-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <RobotOutlined className="text-2xl text-purple-500" />
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Chatbot IA</h1>
              <button
                onClick={() => setShowHelpModal(true)}
                className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full transition-colors"
                title="Ayuda sobre Chatbot IA"
              >
                <InfoCircleOutlined className="text-lg" />
              </button>
            </div>
            <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">Asistentes de Inteligencia Artificial</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl">
              Crea y gestiona chatbots inteligentes que pueden responder preguntas, analizar documentos y asistir a tus usuarios automáticamente.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchServices}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ReloadOutlined className={loading ? 'animate-spin' : ''} />
              Refrescar
            </button>
            <button
              onClick={() => navigate('/chatbot-ia/create')}
              className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-md text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors flex items-center gap-2"
            >
              <PlusOutlined />
              Crear Chatbot
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Buscar por nombre o plan..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none"
            />
          </div>

          {/* Plan Filter */}
          <select
            value={filterPlan}
            onChange={(e) => setFilterPlan(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none"
          >
            <option value="all">Todos los planes</option>
            <option value="starter">Starter</option>
            <option value="pro">Pro</option>
            <option value="enterprise">Enterprise</option>
          </select>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none"
          >
            <option value="all">Todos los estados</option>
            <option value="running">Activo</option>
            <option value="stopped">Detenido</option>
            <option value="deploying">Desplegando</option>
            <option value="error">Error</option>
          </select>
        </div>
      </div>

      {/* Loading */}
      {loading && services.length === 0 && (
        <div className="text-center py-16">
          <ReloadOutlined className="text-3xl text-gray-400 animate-spin mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Cargando servicios...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredServices.length === 0 && (
        <EmptyState
          title="No hay chatbots configurados"
          description={
            searchTerm || filterStatus !== 'all' || filterPlan !== 'all'
              ? 'No se encontraron resultados. Intenta ajustar los filtros de búsqueda.'
              : 'Comienza creando tu primer asistente de inteligencia artificial.'
          }
          icon={<RobotOutlined className="text-4xl text-gray-400" />}
          action={
            !searchTerm && filterStatus === 'all' && filterPlan === 'all'
              ? {
                  label: 'Crear Chatbot',
                  onClick: () => navigate('/chatbot-ia/create'),
                }
              : undefined
          }
        />
      )}

      {/* Services Grid */}
      {!loading && filteredServices.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredServices.map((service) => (
            <ChatbotCard
              key={service.id}
              service={service}
              onClick={() => navigate(`/chatbot-ia/${service.id}`)}
            />
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {!loading && services.length > 0 && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Chatbots</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{services.length}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Activos</div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {services.filter((s) => s.status === 'running').length}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Desplegando</div>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {services.filter((s) => s.status === 'deploying').length}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Detenidos</div>
            <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
              {services.filter((s) => s.status === 'stopped').length}
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      <SideModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title="Acerca de Chatbot IA"
      >
        <div className="space-y-8">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg p-6 border border-purple-200 dark:border-purple-800">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-purple-600 dark:bg-purple-500 rounded-lg flex items-center justify-center">
                <RobotOutlined className="text-2xl text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Chatbot IA</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">Asistentes inteligentes para tu negocio</p>
              </div>
            </div>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Crea chatbots impulsados por inteligencia artificial que pueden responder preguntas,
              analizar documentos PDF y Excel, y asistir a tus usuarios las 24 horas del día.
            </p>
          </div>

          {/* Planes */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center text-purple-600 dark:text-purple-400 font-bold text-sm">1</span>
              Planes disponibles
            </h3>
            <div className="space-y-3 pl-10">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                <h4 className="font-semibold text-gray-900 dark:text-white">Starter - $29/mes</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">500 mensajes, 10 documentos, soporte PDF y Excel.</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-purple-200 dark:border-purple-600">
                <h4 className="font-semibold text-gray-900 dark:text-white">Pro - $99/mes <span className="text-xs text-purple-600 dark:text-purple-400">(Más popular)</span></h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">5,000 mensajes, 100 documentos, imágenes, API y analytics.</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                <h4 className="font-semibold text-gray-900 dark:text-white">Enterprise - $299/mes</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">50,000 mensajes, 1,000 documentos, todas las features, soporte prioritario.</p>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-6 border border-green-200 dark:border-green-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center text-green-600 dark:text-green-400 font-bold text-sm">2</span>
              Características
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-10">
              <div className="flex items-start gap-2">
                <CheckCircleOutlined className="text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300 text-sm">Análisis de documentos PDF y Excel</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircleOutlined className="text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300 text-sm">Widget de chat embebible</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircleOutlined className="text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300 text-sm">API REST para integración</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircleOutlined className="text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300 text-sm">Analytics y métricas de uso</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircleOutlined className="text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300 text-sm">Soporte prioritario Enterprise</span>
              </div>
            </div>
          </div>
        </div>
      </SideModal>
    </div>
  );
};

export default ChatbotIA;
